import React from "react";
import Image from "../../../components/AppImage";
import DecryptedImage from "../../../components/DecryptedImage";

/**
 * Shared modal body for viewing survey report (used in Approvals and Unworkable pages).
 * Renders customer info, survey overview, meter details, roof engineering (incl. Panel Placement),
 * electrical/logistics, obstacles, and all photo sections.
 */
const SurveyReportModalBody = ({
  survey,
  customer,
  getRejectionReasonLabel = (v) => v ?? "—",
}) => {
  if (!survey || !customer) return null;

  const roofFeasibilityDisplay =
    survey.roof_feasible === true
      ? "Feasible"
      : survey.roof_feasible === false
        ? "Not Feasible"
        : "—";

  const customerRows = [
    ["Customer Name", customer.name],
    ["CIN", customer.cin],
    ["USN", customer.usn],
    ["Phone", customer.phone_number],
    ["Aadhaar Number", customer.aadhaar_number],
    [
      "Date of Birth",
      customer.dob ? new Date(customer.dob).toLocaleDateString("en-IN") : null,
    ],
    ["Address", customer.address],
    ["Village", customer.village],
    ["District", customer.district],
    ["State", customer.state],
    ["Pincode", customer.pincode],
    ["Region", customer.regions?.name ?? customer.region],
    ["Cluster", customer.clusters?.name ?? customer.cluster],
    [
      "Coordinates",
      customer.latitude != null && customer.longitude != null
        ? `${customer.latitude}, ${customer.longitude}`
        : null,
    ],
  ].filter(([, v]) => v != null && v !== "");

  const roofEngineeringRows = [
    ["Roof Type", survey.roof_type],
    [
      "Total Roof Area",
      survey.total_roof_area_sqft
        ? `${survey.total_roof_area_sqft} sq ft`
        : null,
    ],
    [
      "Shadow Free Area",
      survey.shadow_free_area_sqft
        ? `${survey.shadow_free_area_sqft} sq ft`
        : null,
    ],
    ["Roof Condition", survey.roof_condition],
    ["Roof Orientation", survey.roof_orientation],
    [
      "Parapet Height",
      survey.parapet_height_ft ? `${survey.parapet_height_ft} ft` : null,
    ],
    ["Number of Floors", survey.number_of_floors],
    ["Access Type", survey.access_type],
    ["Panel Placement", survey.panel_placement],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-6 text-sm">
      {/* Customer Information */}
      <section>
        <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
          Customer Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {customerRows.map(([label, value]) => (
            <div key={label}>
              <span className="text-muted-foreground">{label}:</span>{" "}
              <span className="text-foreground">{String(value)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Survey Overview */}
      <section>
        <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
          Survey Overview
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <div>
            <span className="text-muted-foreground">Roof Feasibility:</span>{" "}
            <span
              className={
                survey.roof_feasible === true
                  ? "text-success"
                  : survey.roof_feasible === false
                    ? "text-destructive"
                    : ""
              }
            >
              {roofFeasibilityDisplay}
            </span>
          </div>
          {survey.roof_feasible === false && survey.roof_rejection_reason && (
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Rejection Reason:</span>{" "}
              {getRejectionReasonLabel(survey.roof_rejection_reason)}
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Survey Completed On:</span>{" "}
            {survey.submitted_at
              ? new Date(survey.submitted_at).toLocaleDateString("en-IN", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "—"}
          </div>
        </div>
      </section>

      {/* Meter Details */}
      {(survey.proposed_capacity_kw || survey.sanctioned_load_kw) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Meter Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {survey.proposed_capacity_kw && (
              <div>
                <span className="text-muted-foreground">
                  Proposed Capacity:
                </span>{" "}
                {survey.proposed_capacity_kw} kW
              </div>
            )}
            {survey.sanctioned_load_kw && (
              <div>
                <span className="text-muted-foreground">Sanctioned Load:</span>{" "}
                {survey.sanctioned_load_kw} kW
              </div>
            )}
            {survey.phase_connection && (
              <div>
                <span className="text-muted-foreground">Phase:</span>{" "}
                {survey.phase_connection}
              </div>
            )}
            {survey.lineman_name && (
              <div>
                <span className="text-muted-foreground">Lineman Name:</span>{" "}
                {survey.lineman_name}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Roof Engineering */}
      {(survey.roof_type ||
        survey.total_roof_area_sqft ||
        survey.panel_placement) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Roof Engineering
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {roofEngineeringRows.map(([label, value]) => (
              <div key={label}>
                <span className="text-muted-foreground">{label}:</span>{" "}
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Electrical & Site Logistics */}
      {(survey.meter_location || survey.internet_status) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Electrical & Site Logistics
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {[
              ["Meter Location", survey.meter_location],
              ["Internet Status", survey.internet_status],
              ["Earthing Soil Type", survey.earthing_soil_type],
            ]
              .filter(([, v]) => v)
              .map(([label, value]) => (
                <div key={label}>
                  <span className="text-muted-foreground">{label}:</span>{" "}
                  <span className="text-foreground">{value}</span>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Obstacles */}
      <section>
        <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
          Obstacles
        </h3>
        <ul className="list-disc list-inside text-sm text-foreground space-y-1">
          {[
            [survey.no_obstacle, "No Obstacle"],
            [survey.water_tank_present, "Water Tank Present"],
            [survey.neighbors_wall_present, "Neighbor's Wall Present"],
            [survey.trees_present, "Trees Present"],
          ]
            .filter(([val]) => val === true)
            .map(([_, label]) => (
              <li key={label}>{label}</li>
            ))}
          {![
            survey.no_obstacle,
            survey.water_tank_present,
            survey.neighbors_wall_present,
            survey.trees_present,
          ].some(Boolean) && <li className="text-muted-foreground">—</li>}
        </ul>
      </section>

      {/* Roof Inspection Photos */}
      {survey.roof_inspection_photos?.length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Roof Inspection Photos
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {survey.roof_inspection_photos.map((url, i) => (
              <div
                key={i}
                className="rounded border border-border overflow-hidden"
              >
                <Image
                  src={url}
                  alt={`Roof inspection ${i + 1}`}
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Photo {i + 1}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Site Photos */}
      {(survey.house_front_view_photos?.length > 0 ||
        survey.meter_photos?.length > 0 ||
        survey.line_diagram_photos?.length > 0 ||
        survey.obstacle_photos?.length > 0) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Site Photos
          </h3>
          <div className="space-y-4">
            {survey.house_front_view_photos?.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs mb-2">
                  House Front View
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {survey.house_front_view_photos.map((url, i) => (
                    <div
                      key={i}
                      className="rounded border border-border overflow-hidden"
                    >
                      <Image
                        src={url}
                        alt={`Roof view ${i + 1}`}
                        className="w-full h-32 object-contain bg-muted"
                      />
                      <p className="text-xs p-1.5 text-center text-muted-foreground">
                        Photo {i + 1}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {survey.meter_photos?.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs mb-2">
                  Meter Photos
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {survey.meter_photos.map((url, i) => (
                    <div
                      key={i}
                      className="rounded border border-border overflow-hidden"
                    >
                      <Image
                        src={url}
                        alt={`Meter ${i + 1}`}
                        className="w-full h-32 object-contain bg-muted"
                      />
                      <p className="text-xs p-1.5 text-center text-muted-foreground">
                        Photo {i + 1}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {survey.line_diagram_photos?.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs mb-2">
                  Line Diagram
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {survey.line_diagram_photos.map((url, i) => (
                    <div
                      key={i}
                      className="rounded border border-border overflow-hidden"
                    >
                      <Image
                        src={url}
                        alt={`Line diagram ${i + 1}`}
                        className="w-full h-32 object-contain bg-muted"
                      />
                      <p className="text-xs p-1.5 text-center text-muted-foreground">
                        Photo {i + 1}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {survey.obstacle_photos?.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs mb-2">
                  Obstacle Photos
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {survey.obstacle_photos.map((url, i) => (
                    <div
                      key={i}
                      className="rounded border border-border overflow-hidden"
                    >
                      <Image
                        src={url}
                        alt={`Obstacle ${i + 1}`}
                        className="w-full h-32 object-contain bg-muted"
                      />
                      <p className="text-xs p-1.5 text-center text-muted-foreground">
                        Photo {i + 1}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Document Photos */}
      {(survey.aadhar_photo ||
        survey.bank_passbook_photo ||
        survey.electricity_bill_photo) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Document Photos
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {survey.aadhar_photo && (
              <div className="rounded border border-border overflow-hidden">
                <DecryptedImage
                  src={survey.aadhar_photo}
                  alt="Aadhar"
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Aadhar
                </p>
              </div>
            )}
            {survey.bank_passbook_photo && (
              <div className="rounded border border-border overflow-hidden">
                <DecryptedImage
                  src={survey.bank_passbook_photo}
                  alt="Bank Passbook"
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Bank Passbook
                </p>
              </div>
            )}
            {survey.electricity_bill_photo && (
              <div className="rounded border border-border overflow-hidden">
                <DecryptedImage
                  src={survey.electricity_bill_photo}
                  alt="Electricity Bill"
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Electricity Bill
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Tentative Location Photos */}
      {(survey.tentative_panel_location_photo ||
        survey.tentative_inverter_location_photo) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Tentative Location
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {survey.tentative_panel_location_photo && (
              <div className="rounded border border-border overflow-hidden">
                <Image
                  src={survey.tentative_panel_location_photo}
                  alt="Panel location"
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Panel location
                </p>
              </div>
            )}
            {survey.tentative_inverter_location_photo && (
              <div className="rounded border border-border overflow-hidden">
                <Image
                  src={survey.tentative_inverter_location_photo}
                  alt="Inverter location"
                  className="w-full h-32 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Inverter location
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Signatures */}
      {(survey.surveyor_signature_photo || survey.customer_signature_photo) && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Signatures
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {survey.surveyor_signature_photo && (
              <div className="rounded border border-border overflow-hidden">
                <Image
                  src={survey.surveyor_signature_photo}
                  alt="Surveyor signature"
                  className="w-full h-24 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Surveyor
                </p>
              </div>
            )}
            {survey.customer_signature_photo && (
              <div className="rounded border border-border overflow-hidden">
                <Image
                  src={survey.customer_signature_photo}
                  alt="Customer signature"
                  className="w-full h-24 object-contain bg-muted"
                />
                <p className="text-xs p-1.5 text-center text-muted-foreground">
                  Customer
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Consent & Sign-off */}
      <section>
        <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
          Consent & Sign-off
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <div>
            <span className="text-muted-foreground">Consent Accepted:</span>{" "}
            {survey.consent_accepted === true
              ? "Yes"
              : survey.consent_accepted === false
                ? "No"
                : "—"}
          </div>
          <div>
            <span className="text-muted-foreground">Surveyor Signed:</span>{" "}
            {survey.surveyor_signed === true
              ? "Yes"
              : survey.surveyor_signed === false
                ? "No"
                : "—"}
          </div>
          <div>
            <span className="text-muted-foreground">Customer Signed:</span>{" "}
            {survey.customer_signed === true
              ? "Yes"
              : survey.customer_signed === false
                ? "No"
                : "—"}
          </div>
        </div>
      </section>

      {survey.survey_notes && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            Notes
          </h3>
          <p className="text-foreground whitespace-pre-wrap">
            {survey.survey_notes}
          </p>
        </section>
      )}

      {survey.qr_code_photo && (
        <section>
          <h3 className="text-base font-semibold text-foreground mb-3 pb-2 border-b border-border">
            QR Code
          </h3>
          <div className="rounded border border-border overflow-hidden inline-block">
            <Image
              src={survey.qr_code_photo}
              alt="QR Code"
              className="w-32 h-32 object-contain bg-muted"
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default SurveyReportModalBody;
